// ---------------------------------------------------------------------------
// SeriesWindow: a janela de velas carregadas de UMA série (ativo + timeframe), em ÍNDICE ANCORADO.
//
// Índice ancorado (Fase A, plano F1–F3):
//   - 0 é a vela mais nova no momento em que a série ABRIU; o índice cresce para o PASSADO (1, 2, 3...);
//   - velas novas que chegam ao vivo depois da abertura ganham -1, -2, ...;
//   - o índice de uma vela NUNCA muda enquanto a série está aberta (cargas de histórico, velas novas e despejo
//     não renumeram nada). Isso é o que a "distância do fim" (usada hoje) não tem: ela muda a cada vela nova.
// A janela guarda `baseAbs` = índice ancorado da vela local 0 (a mais antiga carregada); cada vela local j vale
// `baseAbs - j` (j cresce para o presente).
//
// O HORÁRIO DE ABERTURA (openTime) é a chave de bloco e de busca ("ir ao tempo T"), não o índice: a série tem
// lacunas (manutenção da corretora, mercado fechado nos tradicionais), então horário e índice não são lineares.
//
// Puro (sem React, sem rede): testável em Node. Não guarda nada além do array de velas e de números.
// F1: só o módulo e os testes; o app ainda guarda as velas no próprio array (rawKlinesRef). O despejo (`evict*`) e o
// `goToTime` estão implementados e testados, mas NÃO estão ligados ao app (F3 e UI futura).
// ---------------------------------------------------------------------------

/** Vela no formato bruto da fonte: [openTime, open, high, low, close, volume, closeTime, ...]. Só o [0] é usado aqui. */
export type RawCandle = readonly any[];

/** Velas por bloco (chave de bloco = horário de abertura alinhado a BLOCK_CANDLES velas do timeframe). */
export const BLOCK_CANDLES = 1000;

export interface GoToTimeResult {
  /** índice ancorado da vela com openTime <= T mais próxima (ou da mais antiga, se T é anterior a tudo) */
  abs: number;
  /** existe vela com openTime exatamente T */
  exact: boolean;
  /** T está dentro do trecho carregado */
  loaded: boolean;
  /** se não está carregado, para que lado buscar */
  direction: 'older' | 'newer' | null;
}

export class SeriesWindow {
  readonly symbol: string;
  readonly timeframe: string;
  /** duração de uma vela em ms (para o alinhamento dos blocos) */
  readonly intervalMs: number;
  /** horário da vela que define o índice 0 (a mais nova na abertura); null = série ainda não aberta */
  anchorTime: number | null = null;
  /** índice ancorado da vela local 0 (a mais antiga carregada) */
  baseAbs = 0;
  private candles: RawCandle[] = [];
  /** janela liberada (aba lacrada): sem velas na memória, mas com âncora e limites guardados */
  released = false;
  private releasedSpan: { oldestAbs: number; newestAbs: number; oldestTime: number; newestTime: number } | null = null;

  constructor(symbol: string, timeframe: string, intervalMs: number) {
    if (!(intervalMs > 0)) throw new Error('intervalMs inválido');
    this.symbol = symbol; this.timeframe = timeframe; this.intervalMs = intervalMs;
  }

  // ---------------------------------------------------------------- tamanho e limites
  get length(): number { return this.candles.length; }
  /** índice ancorado da vela mais antiga carregada */
  get oldestAbs(): number { return this.candles.length ? this.baseAbs : (this.releasedSpan ? this.releasedSpan.oldestAbs : 0); }
  /** índice ancorado da vela mais nova carregada (0 na abertura; negativo depois de velas novas ao vivo) */
  get newestAbs(): number { return this.candles.length ? this.baseAbs - (this.candles.length - 1) : (this.releasedSpan ? this.releasedSpan.newestAbs : 0); }
  get oldestTime(): number { return this.candles.length ? this.candles[0][0] : (this.releasedSpan ? this.releasedSpan.oldestTime : NaN); }
  get newestTime(): number { return this.candles.length ? this.candles[this.candles.length - 1][0] : (this.releasedSpan ? this.releasedSpan.newestTime : NaN); }

  // ---------------------------------------------------------------- mapa abs <-> local
  /** índice local (0 = mais antiga carregada) do índice ancorado; pode ficar fora de [0, length) */
  absToLocal(abs: number): number { return this.baseAbs - abs; }
  localToAbs(j: number): number { return this.baseAbs - j; }
  hasAbs(abs: number): boolean { const j = this.absToLocal(abs); return j >= 0 && j < this.candles.length && Number.isInteger(j); }
  /** vela pelo índice ancorado (undefined se não carregada) */
  at(abs: number): RawCandle | undefined { return this.hasAbs(abs) ? this.candles[this.absToLocal(abs)] : undefined; }
  /** distância do fim (o "historyOffset" de hoje) <-> índice ancorado, em relação à vela mais nova carregada */
  absToDistance(abs: number): number { return abs - this.newestAbs; }
  distanceToAbs(distance: number): number { return this.newestAbs + distance; }

  // ---------------------------------------------------------------- abertura e crescimento
  /** Abre a série com as velas iniciais (ordem crescente de horário). A mais nova vira o índice 0. */
  open(candles: readonly RawCandle[]): void {
    const list = this.sortedUnique(candles);
    this.candles = list;
    this.released = false; this.releasedSpan = null;
    this.anchorTime = list.length ? list[list.length - 1][0] : null;
    this.baseAbs = list.length ? list.length - 1 : 0;
  }

  /** Acrescenta velas MAIS ANTIGAS (carga de histórico). Ignora as que não são mais antigas que a primeira. Devolve quantas entraram. */
  prepend(older: readonly RawCandle[]): number {
    if (this.released) throw new Error('janela liberada: reabra antes de carregar');
    if (!this.candles.length) { this.open(older); return this.candles.length; }
    const first = this.candles[0][0];
    const add = this.sortedUnique(older).filter(c => c[0] < first);
    if (!add.length) return 0;
    this.candles = add.concat(this.candles);
    this.baseAbs += add.length;
    return add.length;
  }

  /**
   * Acrescenta velas MAIS NOVAS (ao vivo). A vela com o mesmo horário da última a substitui (vela em formação);
   * as mais novas ganham índices negativos. Devolve quantas velas novas entraram (substituição não conta).
   */
  append(newer: readonly RawCandle[]): number {
    if (this.released) throw new Error('janela liberada: reabra antes de carregar');
    if (!this.candles.length) { this.open(newer); return 0; }
    let added = 0;
    for (const c of this.sortedUnique(newer)) {
      const last = this.candles[this.candles.length - 1][0];
      if (c[0] === last) this.candles[this.candles.length - 1] = c;
      else if (c[0] > last) { this.candles.push(c); added++; }
    }
    return added;     // baseAbs (índice da mais antiga) não muda: as novas ficam em baseAbs - j, j maior
  }

  // ---------------------------------------------------------------- despejo (F3; não ligado no F1)
  /** Remove as `n` velas mais ANTIGAS. Os índices ancorados das que ficam não mudam. Devolve quantas saíram. */
  evictOldest(n: number): number {
    const k = Math.max(0, Math.min(n | 0, this.candles.length));
    if (!k) return 0;
    this.candles = this.candles.slice(k);
    this.baseAbs -= k;
    return k;
  }
  /** Remove as `n` velas mais NOVAS (ex.: a janela foi para o passado distante). Devolve quantas saíram. */
  evictNewest(n: number): number {
    const k = Math.max(0, Math.min(n | 0, this.candles.length));
    if (!k) return 0;
    this.candles = this.candles.slice(0, this.candles.length - k);   // baseAbs (a mais antiga) não muda
    return k;
  }
  /** Mantém só as velas com índice ancorado em [fromAbs, toAbs] (fromAbs <= toAbs; fromAbs mais nova). */
  evictOutside(fromAbs: number, toAbs: number): number {
    if (fromAbs > toAbs) [fromAbs, toAbs] = [toAbs, fromAbs];
    const before = this.candles.length;
    this.evictOldest(this.baseAbs - toAbs);                 // tudo mais antigo que toAbs
    this.evictNewest(fromAbs - this.newestAbs);             // tudo mais novo que fromAbs
    return before - this.candles.length;
  }

  /** Libera as velas da memória (aba lacrada). Guarda âncora e limites; `reopen` volta a aceitar velas. */
  release(): void {
    if (this.candles.length) this.releasedSpan = { oldestAbs: this.oldestAbs, newestAbs: this.newestAbs, oldestTime: this.oldestTime, newestTime: this.newestTime };
    this.candles = [];
    this.released = true;
  }
  /** Reabre uma janela liberada com velas do trecho [fromAbs ...] (recarregadas da fonte), mantendo a âncora. */
  reopen(candles: readonly RawCandle[], oldestAbs: number): void {
    const list = this.sortedUnique(candles);
    this.candles = list;
    this.baseAbs = oldestAbs;
    this.released = false; this.releasedSpan = null;
  }

  // ---------------------------------------------------------------- busca por horário
  /** Índice local da primeira vela com openTime >= t (length se nenhuma). */
  private lowerBound(t: number): number {
    const a = this.candles; let lo = 0, hi = a.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (a[mid][0] < t) lo = mid + 1; else hi = mid; }
    return lo;
  }
  /** Índice ancorado da vela com openTime exatamente t, ou null (lacuna ou fora do carregado). */
  absOfTime(t: number): number | null {
    const j = this.lowerBound(t);
    return j < this.candles.length && this.candles[j][0] === t ? this.localToAbs(j) : null;
  }
  /** Índice ancorado da vela com openTime <= t mais próxima (em lacunas, a anterior); null se t é anterior a tudo. */
  absAtOrBefore(t: number): number | null {
    const j = this.lowerBound(t);
    if (j < this.candles.length && this.candles[j][0] === t) return this.localToAbs(j);
    return j === 0 ? null : this.localToAbs(j - 1);
  }

  /**
   * Ponto de extensão "ir ao tempo T" (sem UI no F1): onde T cai em relação ao carregado.
   * Carregado: abs da vela <= T (exata ou a anterior, em lacuna). Fora: direção para buscar e o abs da ponta mais próxima.
   */
  goToTime(t: number): GoToTimeResult {
    if (!this.candles.length) return { abs: 0, exact: false, loaded: false, direction: this.anchorTime !== null && t > this.anchorTime ? 'newer' : 'older' };
    if (t < this.oldestTime) return { abs: this.oldestAbs, exact: false, loaded: false, direction: 'older' };
    if (t > this.newestTime + this.intervalMs - 1) return { abs: this.newestAbs, exact: false, loaded: false, direction: 'newer' };
    const abs = this.absAtOrBefore(t)!;
    return { abs, exact: this.at(abs)![0] === t, loaded: true, direction: null };
  }

  // ---------------------------------------------------------------- blocos
  /** Chave do bloco de um horário: início alinhado de um bloco de BLOCK_CANDLES velas (independe do que está carregado). */
  blockKey(t: number): number {
    const span = this.intervalMs * BLOCK_CANDLES;
    return Math.floor(t / span) * span;
  }
  /** Blocos (chaves) cobertos pelo trecho carregado, do mais antigo ao mais novo. */
  loadedBlocks(): number[] {
    if (!this.candles.length) return [];
    const out: number[] = [], span = this.intervalMs * BLOCK_CANDLES;
    for (let k = this.blockKey(this.oldestTime); k <= this.newestTime; k += span) out.push(k);
    return out;
  }
  /** Intervalo de horários [início, fim) de um bloco. */
  blockRange(key: number): [number, number] { return [key, key + this.intervalMs * BLOCK_CANDLES]; }

  /** Cópia das velas carregadas (ordem crescente de horário). */
  toArray(): RawCandle[] { return this.candles.slice(); }

  private sortedUnique(list: readonly RawCandle[]): RawCandle[] {
    const out = list.filter(c => c && Number.isFinite(c[0])).slice().sort((a, b) => a[0] - b[0]);
    const u: RawCandle[] = [];
    for (const c of out) { if (u.length && u[u.length - 1][0] === c[0]) u[u.length - 1] = c; else u.push(c); }
    return u;
  }
}

/** Duração de uma vela do timeframe da fonte (intervalo da Binance/Yahoo: '1m', '1h', '1d', '1w', '1M'...). */
export function intervalToMs(interval: string): number {
  const m = /^(\d+)([mhdwM])$/.exec(interval);
  if (!m) return 60_000;
  const n = +m[1];
  const unit = m[2] === 'm' ? 60_000 : m[2] === 'h' ? 3_600_000 : m[2] === 'd' ? 86_400_000 : m[2] === 'w' ? 604_800_000 : 2_592_000_000;
  return n * unit;
}
